<div
  class="eva-select"
  [class.eva-select--open]="isOpen"
  [class.eva-select--error]="error"
  [class.eva-select--disabled]="disabled"
  [class.eva-select--focused]="isFocused"
  [class.eva-select--has-value]="hasValue"
  [class.eva-select--highlight-active]="highlightWhenActive && hasValue"
  [class.eva-select--small]="size === 'small'"
  [class.eva-select--medium]="size === 'medium'"
  [class.eva-select--large]="size === 'large'"
  [class.eva-select--locked]="locked"
  [class.eva-select--filter]="variant === 'filter'"
>
  <!-- Label -->
  <label *ngIf="label" [for]="inputId" class="eva-select__label">
    {{ label }}<span *ngIf="required" class="eva-select__required">*</span>
  </label>

  <!-- Trigger button -->
  <div
    class="eva-select__trigger"
    [attr.id]="inputId"
    [attr.tabindex]="editable ? -1 : disabled ? -1 : 0"
    role="combobox"
    [attr.aria-expanded]="isOpen"
    [attr.aria-haspopup]="true"
    [attr.aria-disabled]="disabled"
    [attr.aria-readonly]="locked ? true : null"
    (mousedown)="editable ? null : onTriggerMousedown($event)"
    (focus)="editable ? null : handleFocus()"
    (blur)="editable ? null : handleBlur()"
    (keydown)="editable ? null : handleKeydown($event)"
  >
    <!-- Icône funnel (variant filter uniquement) -->
    <ng-icon name="heroFunnel" class="eva-select__filter-icon" *ngIf="variant === 'filter'"></ng-icon>

    <!-- Bouton lock/unlock -->
    <button
      *ngIf="lockable"
      type="button"
      class="eva-select__lock-toggle"
      [class.eva-select__lock-toggle--locked]="locked"
      [disabled]="disabled"
      (click)="toggleLock($event)"
      [attr.aria-label]="
        locked ? 'Déverrouiller le champ (Ctrl/Cmd + Maj + L)' : 'Verrouiller le champ (Ctrl/Cmd + Maj + L)'
      "
      [attr.title]="
        locked ? 'Déverrouiller le champ (Ctrl/Cmd + Maj + L)' : 'Verrouiller le champ (Ctrl/Cmd + Maj + L)'
      "
    >
      <ng-icon [name]="locked ? 'heroLockClosed' : 'heroLockOpen'"></ng-icon>
    </button>

    <!-- Mode combobox : input éditable -->
    <input
      *ngIf="editable"
      #editableInput
      class="eva-select__editable-input"
      [value]="editableInputValue"
      [placeholder]="placeholder"
      [disabled]="disabled"
      autocomplete="off"
      (input)="onEditableInput($event)"
      (focus)="onEditableFocus()"
      (blur)="onEditableBlur()"
      (keydown)="onEditableKeydown($event)"
    />

    <!-- Selected value display (mode normal) -->
    <span
      *ngIf="!editable"
      class="eva-select__value"
      [class.eva-select__value--placeholder]="!hasValue"
      [attr.title]="valueTitle"
    >
      <!-- Single select avec avatar -->
      <ng-container *ngIf="showAvatar && !multiple && selectedOption?.avatar">
        <eva-avatar
          class="eva-select__trigger-avatar"
          [src]="selectedOption.avatar.src || ''"
          [name]="selectedOption.label"
          [initialsOverride]="selectedOption.avatar.initials || ''"
          [color]="selectedOption.avatar.color || ''"
          [size]="avatarSize"
        ></eva-avatar>
      </ng-container>

      <!-- Multi-select avec avatars empilés -->
      <ng-container *ngIf="showAvatar && multiple && hasValue">
        <span class="eva-select__avatar-stack">
          <eva-avatar
            *ngFor="let opt of visibleAvatars; let i = index"
            class="eva-select__stacked-avatar"
            [style.z-index]="visibleAvatars.length - i"
            [src]="opt.avatar?.src || ''"
            [name]="opt.label"
            [initialsOverride]="opt.avatar?.initials || ''"
            [color]="opt.avatar?.color || ''"
            [size]="avatarSize"
          ></eva-avatar>
          <span *ngIf="hiddenAvatarsCount > 0" class="eva-select__avatar-overflow"> +{{ hiddenAvatarsCount }} </span>
        </span>
      </ng-container>

      <!-- ============================================================ -->
      <!-- MODE SINGLE + showTag : tag seul (pas de label texte)        -->
      <!-- ============================================================ -->
      <ng-container *ngIf="singleTagOnly">
        <eva-tag
          class="eva-select__trigger-tag"
          [label]="selectedOption!.tag!.label"
          [severity]="selectedOption!.tag!.severity || 'primary'"
          [icon]="selectedOption!.tag!.icon || ''"
          [color]="selectedOption!.tag!.color || ''"
          [variant]="selectedOption!.tag!.variant || 'soft'"
          size="small"
        ></eva-tag>
      </ng-container>

      <!-- ============================================================ -->
      <!-- MODE MULTI + showTag : jusqu'à 2 tags + overflow             -->
      <!-- ============================================================ -->
      <ng-container *ngIf="showTag && multiple && hasValue">
        <span class="eva-select__tag-stack">
          <eva-tag
            *ngFor="let tag of visibleTags"
            class="eva-select__trigger-tag"
            [label]="tag.label"
            [severity]="tag.severity || 'primary'"
            [icon]="tag.icon || ''"
            [color]="tag.color || ''"
            [variant]="tag.variant || 'soft'"
            size="small"
          ></eva-tag>
          <span *ngIf="hiddenTagsCount > 0" class="eva-select__tag-overflow"> +{{ hiddenTagsCount }} </span>
        </span>
      </ng-container>

      <!-- Tag à gauche (single select, option SANS tag visible seul) -->
      <ng-container *ngIf="showTag && tagPosition === 'left' && !multiple && !singleTagOnly && selectedOption?.tag">
        <eva-tag
          class="eva-select__trigger-tag eva-select__trigger-tag--left"
          [label]="selectedOption.tag.label"
          [severity]="selectedOption.tag.severity || 'primary'"
          [icon]="selectedOption.tag.icon || ''"
          [color]="selectedOption.tag.color || ''"
          [variant]="selectedOption.tag.variant || 'soft'"
          size="small"
        ></eva-tag>
      </ng-container>

      <!-- Icône de l'option sélectionnée (single select) -->
      @if (!multiple && selectedOption?.icon && (!showAvatar || !selectedOption?.avatar)) {
        @if (isNgIconName(selectedOption.icon)) {
          <ng-icon
            [name]="selectedOption.icon"
            class="eva-select__trigger-icon"
            [color]="selectedOption.iconColor || null"
          ></ng-icon>
        } @else {
          <i
            [class]="selectedOption.icon"
            class="eva-select__trigger-icon"
            [style.color]="selectedOption.iconColor || null"
          ></i>
        }
      }

      <!-- Texte de la valeur (masqué en single+showTag quand l'option a un tag, masqué en multi+showTag) -->
      <span
        *ngIf="!singleTagOnly && !(showTag && multiple)"
        class="eva-select__value-text"
        [class.eva-select__value-text--with-description]="selectedDescription"
        [style.color]="!multiple && selectedOption?.textColor ? selectedOption.textColor : null"
      >
        {{ hasValue ? displayValue : placeholder }}
      </span>

      <!-- Description de l'option sélectionnée (option.description renseignée) -->
      <span *ngIf="selectedDescription" class="eva-select__value-description">
        {{ selectedDescription }}
      </span>

      <!-- Placeholder quand multi+showTag et pas de valeur -->
      <span *ngIf="showTag && multiple && !hasValue" class="eva-select__value-text">
        {{ placeholder }}
      </span>

      <!-- Tag à droite (single select, option SANS tag visible seul) -->
      <ng-container *ngIf="showTag && tagPosition === 'right' && !multiple && !singleTagOnly && selectedOption?.tag">
        <eva-tag
          class="eva-select__trigger-tag eva-select__trigger-tag--right"
          [label]="selectedOption.tag.label"
          [severity]="selectedOption.tag.severity || 'primary'"
          [icon]="selectedOption.tag.icon || ''"
          [color]="selectedOption.tag.color || ''"
          [variant]="selectedOption.tag.variant || 'soft'"
          size="small"
        ></eva-tag>
      </ng-container>
    </span>

    <!-- Right icons -->
    <span class="eva-select__icons">
      <!-- Clear button -->
      <button
        *ngIf="showClear"
        type="button"
        class="eva-select__clear"
        tabindex="-1"
        (mousedown)="$event.stopPropagation()"
        (click)="clear($event)"
      >
        <ng-icon name="heroXMark"></ng-icon>
      </button>

      <!-- Dropdown arrow -->
      <ng-icon
        name="heroChevronDown"
        class="eva-select__arrow"
        (mousedown)="editable ? onArrowMousedownEditable($event) : null"
      ></ng-icon>
    </span>
  </div>

  <!-- Dropdown panel -->
  <div
    #dropdownPanel
    *ngIf="isOpen"
    class="eva-select__dropdown"
    [class.eva-select__dropdown--fixed]="appendToBody"
    [class.eva-select__dropdown--above]="dropdownAbove"
    [class.eva-select__dropdown--has-footer]="showCreateFooter"
    [class.eva-select__dropdown--small]="size === 'small'"
    [class.eva-select__dropdown--medium]="size === 'medium'"
    [class.eva-select__dropdown--large]="size === 'large'"
    [ngStyle]="appendToBody ? dropdownStyle : null"
  >
    <!-- Search input -->
    <div *ngIf="searchable" class="eva-select__search">
      <ng-icon name="heroMagnifyingGlass" class="eva-select__search-icon"></ng-icon>
      <input
        #searchInput
        type="text"
        class="eva-select__search-input"
        [placeholder]="searchPlaceholder"
        [value]="searchQuery"
        (input)="onSearchInput($event)"
        (keydown)="handleKeydown($event)"
        (click)="$event.stopPropagation()"
      />
    </div>

    <!-- Options list (scrollable) -->
    <ul #optionsList class="eva-select__options" role="listbox" [style.max-height]="dropdownMaxHeight">
      <ng-container *ngFor="let option of filteredOptions; let i = index; trackBy: trackByFn">
        <li
          *ngIf="getGroupHeader(option, i) as groupHeader"
          class="eva-select__group-header"
          [class.eva-select__group-header--selectable]="groupSelectionEnabled"
          [class.eva-select__group-header--selected]="groupSelectionEnabled && isGroupFullySelected(groupHeader)"
          [class.eva-select__group-header--indeterminate]="
            groupSelectionEnabled && isGroupPartiallySelected(groupHeader)
          "
          [attr.role]="groupSelectionEnabled ? 'button' : null"
          [attr.tabindex]="groupSelectionEnabled ? 0 : null"
          (click)="groupSelectionEnabled ? toggleGroup(groupHeader, $event) : null"
        >
          <span
            *ngIf="groupSelectionEnabled"
            class="eva-select__group-checkbox"
            [class.eva-select__group-checkbox--checked]="isGroupFullySelected(groupHeader)"
            [class.eva-select__group-checkbox--indeterminate]="isGroupPartiallySelected(groupHeader)"
            aria-hidden="true"
          >
            <ng-icon name="heroCheck" *ngIf="isGroupFullySelected(groupHeader)"></ng-icon>
            <ng-icon
              name="heroMinus"
              *ngIf="!isGroupFullySelected(groupHeader) && isGroupPartiallySelected(groupHeader)"
            ></ng-icon>
          </span>
          <span class="eva-select__group-header-label">{{ groupHeader }}</span>
        </li>
        <li
          class="eva-select__option"
          [class.eva-select__option--selected]="isSelected(option)"
          [class.eva-select__option--disabled]="isOptionDisabled(option)"
          [class.eva-select__option--highlighted]="highlightedIndex === i"
          role="option"
          [attr.aria-selected]="isSelected(option)"
          [attr.aria-disabled]="isOptionDisabled(option)"
          [attr.title]="optionTitle(option)"
          (click)="selectOption(option)"
          (mouseenter)="highlightedIndex = i"
        >
          <!-- Checkbox for multi-select -->
          <span *ngIf="multiple" class="eva-select__option-checkbox">
            <ng-icon name="heroCheck" *ngIf="isSelected(option)"></ng-icon>
          </span>

          <!-- Option avatar -->
          <eva-avatar
            *ngIf="showAvatar && option.avatar"
            class="eva-select__option-avatar"
            [src]="option.avatar.src || ''"
            [name]="getOptionLabel(option)"
            [initialsOverride]="option.avatar.initials || ''"
            [color]="option.avatar.color || ''"
            [size]="avatarSize"
          ></eva-avatar>

          <!-- Option icon (si pas d'avatar) -->
          @if (option.icon && (!showAvatar || !option.avatar)) {
            @if (isNgIconName(option.icon)) {
              <ng-icon
                [name]="option.icon"
                class="eva-select__option-icon"
                [color]="option.iconColor || null"
              ></ng-icon>
            } @else {
              <i [class]="option.icon" class="eva-select__option-icon" [style.color]="option.iconColor || null"></i>
            }
          }

          <!-- Tag à gauche -->
          <ng-container *ngIf="showTag && tagPosition === 'left' && option.tag">
            <eva-tag
              class="eva-select__option-tag eva-select__option-tag--left"
              [label]="option.tag.label"
              [severity]="option.tag.severity || 'primary'"
              [icon]="option.tag.icon || ''"
              [color]="option.tag.color || ''"
              [variant]="option.tag.variant || 'soft'"
              size="small"
            ></eva-tag>
          </ng-container>

          <!-- Option label — masqué quand showTag + option.tag (le tag remplace le label),
               sauf si tagWithLabel (label ET tag affichés côte à côte) -->
          <span *ngIf="!(showTag && option.tag) || tagWithLabel" class="eva-select__option-text">
            <span class="eva-select__option-label" [style.color]="option.textColor || null">{{
              getOptionLabel(option)
            }}</span>
            <span *ngIf="option.description" class="eva-select__option-description">{{ option.description }}</span>
          </span>

          <!-- Tag à droite -->
          <ng-container *ngIf="showTag && tagPosition === 'right' && option.tag">
            <eva-tag
              class="eva-select__option-tag eva-select__option-tag--right"
              [label]="option.tag.label"
              [severity]="option.tag.severity || 'primary'"
              [icon]="option.tag.icon || ''"
              [color]="option.tag.color || ''"
              [variant]="option.tag.variant || 'soft'"
              size="small"
            ></eva-tag>
          </ng-container>
        </li>
      </ng-container>

      <!-- Option de création (mode suggestion) -->
      <li
        *ngIf="showCreateSuggestion"
        class="eva-select__option eva-select__option--create"
        role="option"
        (click)="handleCreateClick()"
      >
        <ng-icon name="heroPlus" class="eva-select__create-icon"></ng-icon>
        <span class="eva-select__create-label">Créer «{{ searchQuery }}»</span>
      </li>

      <!-- Empty state (uniquement si pas d'option de création en mode suggestion et pas de footer) -->
      <li *ngIf="filteredOptions.length === 0 && !showCreateSuggestion && !showCreateFooter" class="eva-select__empty">
        {{ emptyMessage }}
      </li>
    </ul>

    <!-- Footer de création (EN DEHORS du scroll) -->
    <div *ngIf="showCreateFooter" class="eva-select__create-footer" (click)="handleFooterCreateClick()">
      @if (isNgIconName(createIcon)) {
        <ng-icon [name]="createIcon" class="eva-select__create-footer-icon"></ng-icon>
      } @else {
        <i [class]="createIcon + ' eva-select__create-footer-icon'"></i>
      }
      <span class="eva-select__create-footer-label">{{ createLabel }}</span>
    </div>
  </div>

  <!-- Helper text (affiché si pas d'erreur) -->
  <small *ngIf="helper && !error" class="eva-select__helper">
    {{ helper }}
  </small>

  <!-- Error message -->
  <small *ngIf="error" class="eva-select__error-text">
    {{ error }}
  </small>
</div>
